import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { TestPageDataService } from '../services/testPageDataService';
import { TestPageState, MockData, ProspectData } from '../types/testPageTypes';
import { tracker } from '../../../lib/tracker';
import { getAIRecommendations } from '../../../lib/aiRecommendationsService';
import { supabase } from '../../../lib/supabase';
import { isDemoId } from '../../../utils/demoUtils';
import { callEdge } from '../../../lib/apiClient';


const mockData: MockData = {
  pageSpeed: { mobile: 65, desktop: 85 },
  digitalScore: 68,
  recommendations: [
    "Améliorer le temps de chargement des pages sur mobile pour une meilleure expérience utilisateur",
    "Optimiser le contenu pour les mots-clés ciblés afin d'améliorer le positionnement sur les moteurs de recherche",
    "Ajouter une stratégie de contenu régulière pour maintenir l'engagement des visiteurs"
  ]
};

export const useTestPageData = () => {
  const { token } = useParams<{ token: string }>();
  const loadingRef = useRef(false); // Prevent double-loading in StrictMode

  const [state, setState] = useState<TestPageState>({
    loading: true,
    isLoadingAi: false,
    isLoadingIntro: false,
    prospect: null,
    cmsData: null,
    pagespeedData: null,
    keywordsData: [],
    aiRecommendations: [],
    personalizedIntro: null,
    error: null,
    aiError: null,
    keyComponentKey: 0
  });

  // Fonction pour mettre à jour l'état partiellement
  const updateState = useCallback((updates: Partial<TestPageState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Chargement des données du prospect (une seule fois - toutes les analyses sont déjà terminées)
  const loadProspectData = useCallback(async () => {
    if (!token) {
      updateState({
        error: 'Aucun token fourni dans l\'URL.',
        loading: false
      });
      return;
    }

    // Prevent duplicate calls in React StrictMode
    if (loadingRef.current) {
      console.log('🚫 Chargement déjà en cours, ignoré');
      return;
    }

    loadingRef.current = true;

    try {
      updateState({ loading: true, error: null });
      // Tracker la vue de page
      tracker.trackPageView(token);

      // Valider et récupérer le prospect
      const prospectData = await TestPageDataService.validateRouteToken(token);

      if (!prospectData) {
        updateState({
          error: 'Impossible de charger les données du prospect. Le token est peut-être invalide ou expiré.',
          loading: false
        });
        return;
      }

      updateState({ prospect: prospectData });

      // Charger toutes les données via l'Edge Function unifiée
      console.log(`🔍 Chargement des données pour le prospect ${prospectData.id}`);

      const allData = await callEdge<{ prospectId: string }, any>('get-prospect-data', {
        prospectId: prospectData.id
      });

      console.log(`📊 Données chargées:`, {
        cmsData: !!allData.cmsData,
        pagespeedData: {
          exists: !!allData.pagespeedData,
          mobile: allData.pagespeedData?.mobile_performance_score,
          desktop: allData.pagespeedData?.desktop_performance_score
        },
        keywordsData: {
          exists: !!allData.keywordsData,
          length: allData.keywordsData?.length || 0
        }
      });

      updateState({
        cmsData: allData.cmsData,
        pagespeedData: allData.pagespeedData,
        keywordsData: allData.keywordsData,
        loading: false
      });

      // Charger les recommandations AI (elles doivent déjà être disponibles)
      await loadAIRecommendations(prospectData, allData.pagespeedData, allData.cmsData, allData.keywordsData);

    } catch (err: any) {
      console.error('Erreur lors du chargement des données:', err);
      updateState({
        error: 'Une erreur inattendue est survenue.',
        loading: false
      });
      loadingRef.current = false; // Reset on error
    }
  }, [token, updateState]);

  // Chargement des recommandations AI (lecture seule depuis la base de données)
  const loadAIRecommendations = useCallback(async (
    prospectData: ProspectData,
    pagespeedData: any,
    cmsData: any,
    keywordsData: any[]
  ) => {
    try {
      updateState({ isLoadingAi: true, aiError: null });

      // UNIQUEMENT lire les recommandations existantes (générées après form submit par AutoAnalysisService)
      const existingRecommendations = await getAIRecommendations(prospectData.id);

      if (existingRecommendations && existingRecommendations.length > 0) {
        updateState({
          aiRecommendations: existingRecommendations,
          isLoadingAi: false
        });
      } else {
        // Si pas encore de recommandations, utiliser les données mock en attendant
        updateState({
          aiRecommendations: mockData.recommendations,
          isLoadingAi: false
        });
      }

    } catch (error: any) {
      updateState({
        aiError: error.message || 'Erreur lors du chargement des recommandations',
        aiRecommendations: mockData.recommendations,
        isLoadingAi: false
      });
    }
  }, [updateState]);

  // Fonction pour rafraîchir les recommandations AI
  const refreshAIRecommendations = useCallback(async () => {
    if (!state.prospect) return;
    
    await loadAIRecommendations(
      state.prospect, 
      state.pagespeedData, 
      state.cmsData, 
      state.keywordsData
    );
  }, [state.prospect, state.pagespeedData, state.cmsData, state.keywordsData, loadAIRecommendations]);

  // Fonction pour démarrer le quiz
  const startQuiz = useCallback(async () => {
    if (!state.prospect) return false;
    
    const success = await TestPageDataService.updateQuizStatus(state.prospect.id, 'quiz_commence');
    if (success) {
      // Mettre à jour l'état local
      updateState({
        prospect: state.prospect ? { ...state.prospect, quiz_commence: true } : null
      });
      // Rediriger vers la page du quiz
      window.location.href = `/quiz/${token || ''}`;
      return true;
    }
    return false;
  }, [state.prospect, token, updateState]);

  // Fonction pour rafraîchir les avis Google
  const refreshGoogleReviews = useCallback(async () => {
    if (!state.prospect?.id) return;
    
    try {
      console.log('Rafraîchissement des avis Google pour le prospect:', state.prospect.id);
      
      // Vérifier si c'est un ID de démonstration
      if (isDemoId(state.prospect.id)) {
        console.log('ID de démonstration détecté, simulation du rafraîchissement des avis Google');
        // Recharger le composant
        updateState({ keyComponentKey: state.keyComponentKey + 1 });
        return;
      }

      // Vérifier si des données existent déjà
      const { data: existingData } = await supabase
        .from('google_reviews_results')
        .select('id')
        .eq('prospect_id', state.prospect.id)
        .maybeSingle();
      
      if (existingData) {
        console.log('Données Google Reviews existantes trouvées, pas besoin de les supprimer');
      } else {
        console.log('Aucune donnée Google Reviews existante trouvée');
      }
        
      // Forcer un nouveau rendu du composant GoogleReviewsBox en incrémentant la clé
      console.log('Incrémentation de la clé du composant pour forcer un nouveau rendu');
      updateState({ keyComponentKey: state.keyComponentKey + 1 });
      
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des avis Google:', error);
    }
  }, [state.prospect?.id, state.keyComponentKey, updateState]);

  // Fonction pour récupérer une introduction personnalisée existante de la base de données
  const fetchPersonalizedIntro = useCallback(async (prospectId: string) => {
    try {
      // Vérifier si c'est un ID de démonstration
      if (isDemoId(prospectId)) {
        console.log('ID de démonstration détecté, retour d\'une introduction personnalisée de démo');
        return null; // Retourner null pour forcer la génération d'une nouvelle intro
      }

      const { data, error } = await supabase
        .from('personalized_intros')
        .select('content')
        .eq('prospect_id', prospectId)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') { // Code pour "aucun résultat trouvé"
          console.error('Erreur lors de la récupération de l\'introduction personnalisée:', error);
        }
        return null;
      }

      return data?.content || null;
    } catch (err) {
      console.error('Exception lors de la récupération de l\'introduction personnalisée:', err);
      return null;
    }
  }, []);

  // Fonction pour détecter si une introduction est générique
  const isGenericIntro = useCallback((intro: string): boolean => {
    if (!intro) return false;
    // Détection basique pour les introductions génériques
    const genericKeywords = [
      'notre analyse',
      'découvrez vos résultats',
      'améliorer votre présence digitale',
      'cette introduction sera générée automatiquement',
      'fonction des données collectées'
    ];
    return genericKeywords.some(keyword => intro.toLowerCase().includes(keyword.toLowerCase()));
  }, []);

  // Fonction pour charger une introduction personnalisée depuis la base de données (READ-ONLY)
  const loadPersonalizedIntro = useCallback(async (prospectData?: any) => {
    const currentProspect = prospectData || state.prospect;

    if (!currentProspect?.id) {
      console.error('Impossible de charger une introduction sans prospect', { currentProspect, stateProspect: state.prospect });
      return;
    }

    try {
      // Mettre à jour l'état pour indiquer le chargement de l'intro
      updateState({ isLoadingIntro: true });

      // Vérifier si une introduction existe déjà dans la base de données
      const existingIntro = await fetchPersonalizedIntro(currentProspect.id);

      if (existingIntro && !isGenericIntro(existingIntro)) {
        console.log('📝 Introduction personnalisée existante trouvée (non générique)');
        updateState({ personalizedIntro: existingIntro, isLoadingIntro: false });
        return;
      }

      // Si pas d'intro ou intro générique, utiliser un placeholder simple
      console.log('⏳ Aucune introduction personnalisée disponible, utilisation du placeholder');
      const placeholderIntro = 'Analyse en cours...';
      updateState({ personalizedIntro: placeholderIntro, isLoadingIntro: false });

    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'introduction personnalisée:', error);
      // Fallback en cas d'erreur
      updateState({ personalizedIntro: 'Analyse en cours...', isLoadingIntro: false });
    }
  }, [updateState, fetchPersonalizedIntro, isGenericIntro]);

  // Effet pour charger les données une seule fois au montage (toutes les analyses sont déjà terminées)
  useEffect(() => {
    loadProspectData();
  }, [loadProspectData]);

  // Effet pour charger l'introduction personnalisée quand les données sont disponibles
  useEffect(() => {
    if (!state.prospect?.id || state.loading || state.isLoadingIntro) return;

    // Cas 1: Pas d'intro - charger depuis la base de données
    // Cas 2: Intro existante mais générique - tenter de recharger
    const shouldLoadIntro = !state.personalizedIntro || isGenericIntro(state.personalizedIntro);

    if (shouldLoadIntro) {
      console.log('🔄 Chargement d\'intro nécessaire:', {
        hasIntro: !!state.personalizedIntro,
        isGeneric: state.personalizedIntro ? isGenericIntro(state.personalizedIntro) : false,
      });

      // Charger l'introduction depuis la base de données (READ-ONLY)
      loadPersonalizedIntro(state.prospect);
    } else {
      console.log('✅ Introduction personnalisée déjà disponible et non-générique');
    }
  }, [state.prospect?.id, state.loading, state.personalizedIntro, state.isLoadingIntro, loadPersonalizedIntro, isGenericIntro]);

  // Fonction pour forcer le rechargement de l'introduction (utile pour debug)
  const forceRegenerateIntro = useCallback(async () => {
    if (!state.prospect?.id) return;

    try {
      console.log('🔄 Rechargement de l\'introduction...');

      // Recharger l'introduction depuis la base de données
      await loadPersonalizedIntro(state.prospect);

    } catch (error) {
      console.error('❌ Erreur lors du rechargement forcé:', error);
    }
  }, [state.prospect?.id, loadPersonalizedIntro]);

  return {
    // État
    ...state,
    mockData,

    // Actions
    refreshAIRecommendations,
    startQuiz,
    refreshGoogleReviews,
    forceRegenerateIntro,

    // Utilitaires
    updateState
  };
};
